import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { NotificationItem } from "@/components/notifications/NotificationItem";
import { notificationService } from "@/services/notificationService";
import { useAuth } from "@/context/AuthContext";

/** Top-bar Notification Center: unread badge, recent notifications, mark read /
 * mark all read / dismiss, CTA routing, and a link to the full page. */
export const NotificationsPanel = () => {
  const navigate = useNavigate();
  const { platformRole } = useAuth();
  const isStaff = Boolean(platformRole);
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const refreshCount = useCallback(async () => {
    if (isStaff) return;  // workspace-less staff have no customer notification center
    try { setUnread(await notificationService.unreadCount()); } catch { /* ignore */ }
  }, [isStaff]);

  const loadList = useCallback(async () => {
    if (isStaff) return;
    setLoading(true);
    try { setItems((await notificationService.list({ limit: 8 })).notifications); } catch { setItems([]); }
    finally { setLoading(false); }
  }, [isStaff]);

  useEffect(() => { refreshCount(); }, [refreshCount]);
  useEffect(() => {
    const id = setInterval(refreshCount, 60000);
    const onFocus = () => refreshCount();
    window.addEventListener("focus", onFocus);
    window.addEventListener("notifications:changed", refreshCount);
    window.addEventListener("crm:changed", refreshCount);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); window.removeEventListener("notifications:changed", refreshCount); window.removeEventListener("crm:changed", refreshCount); };
  }, [refreshCount]);
  useEffect(() => { if (open) loadList(); }, [open, loadList]);

  if (isStaff) return null;

  const onRead = async (id) => { await notificationService.markRead(id); await Promise.all([loadList(), refreshCount()]); };
  const onDismiss = async (id) => { setItems((l) => l.filter((n) => n.id !== id)); await notificationService.dismiss(id); refreshCount(); };
  const markAll = async () => { await notificationService.markAllRead(); await Promise.all([loadList(), refreshCount()]); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="notifications-trigger"
          className="relative rounded-md p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`}
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span data-testid="notifications-badge" className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-brand-burgundy px-1 text-[10px] font-bold leading-none text-white">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[22rem] p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <p className="text-sm font-semibold text-foreground">Notifications</p>
          {items.some((n) => !n.read_at) && (
            <button onClick={markAll} data-testid="notifications-mark-all" className="flex items-center gap-1 text-xs font-medium text-accent hover:underline">
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          )}
        </div>

        <div className="max-h-[26rem] overflow-y-auto">
          {loading ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</p>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-6 py-10 text-center" data-testid="notifications-empty">
              <div className="grid h-11 w-11 place-items-center rounded-full bg-secondary text-muted-foreground"><Bell className="h-5 w-5" /></div>
              <p className="text-sm font-semibold text-foreground">You're all caught up.</p>
              <p className="text-xs text-muted-foreground">Important updates about your business will appear here.</p>
            </div>
          ) : (
            items.map((n) => <NotificationItem key={n.id} n={n} onRead={onRead} onDismiss={onDismiss} onAfterNavigate={() => setOpen(false)} />)
          )}
        </div>

        <div className="border-t border-border px-4 py-2.5 text-center">
          <button onClick={() => { setOpen(false); navigate("/notifications"); }} data-testid="notifications-view-all" className="text-xs font-medium text-accent hover:underline">
            View all notifications
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
